import axios, { type AxiosResponse } from "axios";
import KnownHttpHeaders from "features/Application/services/knownHttpHeaders";
import { type EnrollmentRule } from "features/People/Groups/AddEditGroupForms/EnrollmentBuilder/utils";
import { type GetOwnersResponse } from "features/People/types";
import { type PageOfItems } from "interfaces";
import { type GetPagedDataRequestV2 } from "interfaces/getPagedDataRequest";
import { isEmpty } from "lodash";
import buildQuery from "odata-query";
import { type DistributedOperationResult } from "types";
import {
  buildQueryFromArray,
  overviewFiltering,
  processPerformanceParams,
  serializeArrayAndFilterNullable,
} from "utils/queryUtils";
import { type TemplateConfigurationView } from "../state/GroupTemplateSlice";
import {
  type AccountPack,
  type AvailableTemplateResponse,
  type ContentResponse,
  type Customer,
  type CustomerEnrollmentsResponse,
  type CustomerEnrollmentsStatisticsResponse,
  type CustomerEnrollmentsSummaryResponse,
  type GetAvailableCustomersRequest,
  type GetAvailableTemplatesRequest,
  type GetPacksRequest,
  type GetTemplateInfoResponse,
  type Pack,
  type PackLicenseInfo,
  type TemplateOverviewResponse,
  type TemplatePackAccounts,
  type TemplatePacks,
  type MostActiveCustomerResponse,
  type ActivityDataPoint,
  type UserActivityCardReportingData,
  type TemplateEnrollmentLineChart,
  type TemplateEmailEngagementCards,
  type TemplateEnrollmentCardData,
  type TemplateFlowEngagement,
  type TemplatePrioritizedFlowCards,
  type PrioritizedFlowTableTemplates,
} from "./types";
import { type BasePerformanceRequestFilterParams, type PaginationParams } from "features/Library/Common/models";
import http from "lib/http";
import StatusCode from "enums/httpStatusCodes";

export const TEMPLATE_LINECHART_ENDPOINT = "/api/reports/v1/templates/linechart";
export const TEMPLATE_ENGAGEMENT_ENDPOINT = "/api/reports/v1/templates/engagement";
export const TEMPLATE_SUMMARY_ENDPOINT = "/api/reports/v1/templates/summary";
export const TEMPLATE_CUSTOMERS_ENDPOINT = "/api/reports/v1/templates/customers";
export const TEMPLATE_USER_ACTIVITY_LINECHART_ENDPOINT = "/api/reports/v1/templates/user/activity/linechart";
export const TEMPLATE_USER_ACTIVITY_CARDS_ENDPOINT = "/api/reports/v1/templates/user/activity/cards";
export const TEMPLATE_USER_ENROLLMENT_CARDS_ENDPOINT = "/api/reports/v1/templates/user/enrollment/cards";
export const TEMPLATE_USER_ENROLLMENT_LINECHART_ENDPOINT = "/api/reports/v1/templates/user/enrollment/linechart";
export const TEMPLATE_ENGAGEMENT_FLOWS_ENDPOINT = "/api/reports/v1/templates/engagement/flows";
export const TEMPLATE_ENGAGEMENT_EMAILS_ENDPOINT = "/api/reports/v1/templates/engagement/emails";
export const TEMPLATE_FLOWS_CARDS_ENDPOINT = "/api/reports/v1/templates/flows/cards";
export const TEMPLATE_FLOWS_TABLE_ENDPOINT = "/api/reports/v2/templates/flows/table";
export const TEMPLATE_EXPORT_ENDPOINT = "/api/dataexport/v1/templates/export";

export const groupTemplateDataService = {
  async getInheritedGroups(request: GetPagedDataRequestV2) {
    const { data, headers } = await axios.get<TemplateOverviewResponse[]>("/api/template-groups", {
      params: request,
    });
    return {
      items: data,
      count: parseInt(headers[KnownHttpHeaders.RecordsCount]),
    };
  },
  async getTemplates(request: GetPagedDataRequestV2) {
    const { data, headers } = await axios.get<TemplateOverviewResponse[]>("/api/templates", {
      params: request,
    });
    return {
      items: data,
      count: parseInt(headers[KnownHttpHeaders.RecordsCount]),
    };
  },
  async getAvailableTemplates(request: GetAvailableTemplatesRequest) {
    const { data, headers } = await axios.get<AvailableTemplateResponse[]>("/api/template-groups/available", {
      params: request,
    });
    return {
      items: data,
      count: parseInt(headers[KnownHttpHeaders.RecordsCount]),
    };
  },
  getTemplateInfo(templateId: number, isPreview?: boolean) {
    return axios.get<GetTemplateInfoResponse>(
      isPreview ? `/api/templates/${templateId}/preview` : `/api/templates/${templateId}/info`,
    );
  },
  updateTemplate(templateId: number, data: TemplateConfigurationView) {
    return axios.put(`/api/templates/${templateId}/info`, data);
  },
  createTemplate(data: TemplateConfigurationView) {
    return axios.post("/api/templates", data);
  },
  deleteTemplate(id: number) {
    return axios.delete(`api/template-groups/${id}`);
  },
  updateTemplateEnrollmentRules(
    templateId: number,
    enrollmentRules: EnrollmentRule[],
    templateUsersOnly?: boolean | null,
  ) {
    return axios.put(`/api/templates/${templateId}/rules`, {
      rules: enrollmentRules,
      templateUsersOnly,
    });
  },
  getAvailableCustomers(request: GetAvailableCustomersRequest) {
    return axios.get<PageOfItems<Customer>>("/api/accounts/customers", {
      params: {
        top: request.top,
        skip: request.skip,
        sortBy: request.sortBy,
        sortOrder: request.sortOrder,
        searchTerm: !isEmpty(request.term) ? request.term : undefined,
        templateId: request.templateId,
      },
      paramsSerializer: overviewFiltering,
    });
  },
  getCustomers(id: number, request: GetPagedDataRequestV2) {
    return axios.get<PageOfItems<Customer>>(`/api/template-groups/${id}/accounts`, {
      params: {
        top: request.top,
        skip: request.skip,
        sortBy: request.sortBy,
        sortOrder: request.sortOrder,
        searchTerm: !isEmpty(request.term) ? request.term : undefined,
      },
    });
  },
  saveCustomers(templateId: number, customers: number[]) {
    return axios.post(`/api/template-groups/${templateId}/accounts`, {
      accountIds: customers,
    });
  },
  removeCustomers(templateId: number, customers: number[]) {
    return axios.delete(`/api/template-groups/${templateId}/accounts`, {
      data: {
        accountIds: customers,
      },
    });
  },
  getFlows(templateId: number, request: GetPagedDataRequestV2) {
    return axios.get<ContentResponse[]>(`/api/template-groups/${templateId}/flows/preview`, {
      params: {
        ...request,
        term: !isEmpty(request.term) ? request.term : undefined,
      },
      paramsSerializer: serializeArrayAndFilterNullable,
    });
  },
  getContent(templateId: number, type: string, request: GetPagedDataRequestV2) {
    return axios.get<ContentResponse[]>(`/api/template-groups/${templateId}/content`, {
      params: {
        ...request,
        term: !isEmpty(request.term) ? request.term : undefined,
        showPurshased: true,
        contentType: type,
      },
      paramsSerializer: serializeArrayAndFilterNullable,
    });
  },
  getAvailablePacks(id: number, request: GetPacksRequest, signal?: AbortSignal): Promise<AxiosResponse<Pack[]>> {
    const config = {
      params: request,
      paramsSerializer: serializeArrayAndFilterNullable,
      signal,
    };

    return axios.get<Pack[]>(`/api/v2/template-groups/${id}/available-packs`, config);
  },
  getPacks(id: number, request: GetPagedDataRequestV2) {
    return axios.get<Pack[]>(`/api/template-groups/${id}/packs`, {
      params: {
        top: request.top,
        skip: request.skip,
        sortBy: request.sortBy,
        sortOrder: request.sortOrder,
      },
    });
  },
  async savePacks(templateId: number, groupPacks: TemplatePackAccounts[]): Promise<DistributedOperationResult> {
    const response = await axios.post(`/api/template-groups/${templateId}/packs`, { templateGroupPacks: groupPacks });
    return response.data;
  },
  async removePacks(templateId: number, packIds: number[]): Promise<DistributedOperationResult> {
    const response = await axios.delete(`/api/template-groups/${templateId}/packs`, { data: { packIds } });
    return response.data;
  },

  approve(templateId: number) {
    return axios.post(`/api/template-groups/${templateId}/approve`);
  },

  decline(templateId: number) {
    return axios.post(`/api/template-groups/${templateId}/decline`);
  },
  getAccountPacks(templateId: number, accountId: number) {
    return axios.get<AccountPack[]>(`/api/template-groups/${templateId}/accounts/${accountId}/packs`);
  },
  async isUniqueTemplateName(name: string) {
    const response = await http.head(`api/templates/${name}`);
    return !response.isSuccess && response.code === StatusCode.NotFound;
  },
  async saveAccountPacks(
    templateId: number,
    accountId: number,
    packs: PackLicenseInfo[],
  ): Promise<DistributedOperationResult> {
    const response = await axios.put(`/api/template-groups/${templateId}/accounts/${accountId}/packs`, {
      packs,
    });
    return response.data;
  },
  async savePackAccounts(
    templateId: number,
    packId: number,
    expirationDate?: Date | null,
  ): Promise<DistributedOperationResult> {
    const response = await axios.put(`/api/template-groups/${templateId}/packs/${packId}/accounts`, {
      expirationDate,
    });
    return response.data;
  },
  getPacksWithTrialInfo(templateId: number) {
    return axios.get<Pack[]>(`/api/template-groups/${templateId}/packs-trial-info`);
  },
  getTemplatesPacks(templateIds: number[]) {
    const query = buildQueryFromArray("templateIds", templateIds);
    return axios.get<AccountPack[]>(`/api/template-groups/packs?${query}`);
  },
  async saveTemplatesPacks(accountId: number, templatesPacks: TemplatePacks[]): Promise<DistributedOperationResult> {
    const response = await axios.post(`/api/template-groups/accounts/${accountId}/packs`, {
      templatesPacks,
    });
    return response.data;
  },
  // Performance
  getTemplateCustomerEnrollmentsLineChart(templateId: number, filter?: BasePerformanceRequestFilterParams) {
    return axios.get<CustomerEnrollmentsResponse[]>(TEMPLATE_LINECHART_ENDPOINT, {
      params: {
        templateId,
        ...filter,
      },
      paramsSerializer: processPerformanceParams,
    });
  },
  getTemplateCustomerEnrollmentsStatistics(templateId: number, filter?: BasePerformanceRequestFilterParams) {
    return axios.get<CustomerEnrollmentsStatisticsResponse>(TEMPLATE_ENGAGEMENT_ENDPOINT, {
      params: {
        templateId,
        ...filter,
      },
      paramsSerializer: processPerformanceParams,
    });
  },
  getTemplateCustomerEnrollmentsSummary(templateId: number, filter?: BasePerformanceRequestFilterParams) {
    return axios.get<CustomerEnrollmentsSummaryResponse>(TEMPLATE_SUMMARY_ENDPOINT, {
      params: {
        templateId,
        ...filter,
      },
      paramsSerializer: processPerformanceParams,
    });
  },
  getMostActiveCustomers(templateId: number, filter?: BasePerformanceRequestFilterParams) {
    return axios.get<MostActiveCustomerResponse[]>(TEMPLATE_CUSTOMERS_ENDPOINT, {
      params: {
        templateId,
        ...filter,
      },
      paramsSerializer: processPerformanceParams,
    });
  },
  getTemplateActivityData(templateId: number, filter?: BasePerformanceRequestFilterParams) {
    return axios.get<ActivityDataPoint[]>(TEMPLATE_USER_ACTIVITY_LINECHART_ENDPOINT, {
      params: {
        templateId,
        ...filter,
      },
      paramsSerializer: processPerformanceParams,
    });
  },
  getTemplateUserActivityCardData(templateId: number, filter?: BasePerformanceRequestFilterParams) {
    return axios.get<UserActivityCardReportingData>(TEMPLATE_USER_ACTIVITY_CARDS_ENDPOINT, {
      params: { templateId, ...filter },
      paramsSerializer: processPerformanceParams,
    });
  },
  getTemplateEnrollmentCardData(templateId: number, filter?: BasePerformanceRequestFilterParams) {
    return axios.get<TemplateEnrollmentCardData>(TEMPLATE_USER_ENROLLMENT_CARDS_ENDPOINT, {
      params: { templateId, ...filter },
      paramsSerializer: processPerformanceParams,
    });
  },
  getTemplateEnrollmentLineChart(templateId: number, filter?: BasePerformanceRequestFilterParams) {
    return axios.get<TemplateEnrollmentLineChart[]>(TEMPLATE_USER_ENROLLMENT_LINECHART_ENDPOINT, {
      params: { templateId, ...filter },
      paramsSerializer: processPerformanceParams,
    });
  },
  getTemplateFlowEngagementResponse(templateId: number, filter?: BasePerformanceRequestFilterParams) {
    return axios.get<TemplateFlowEngagement>(TEMPLATE_ENGAGEMENT_FLOWS_ENDPOINT, {
      params: { templateId, ...filter },
      paramsSerializer: processPerformanceParams,
    });
  },
  getTemplateEmailEngagement(templateId: number, filter?: BasePerformanceRequestFilterParams) {
    return axios.get<TemplateEmailEngagementCards>(TEMPLATE_ENGAGEMENT_EMAILS_ENDPOINT, {
      params: { templateId, ...filter },
      paramsSerializer: processPerformanceParams,
    });
  },
  getTemplatePrioritizedFlowCards(templateId: number, filter?: BasePerformanceRequestFilterParams) {
    return axios.get<TemplatePrioritizedFlowCards>(TEMPLATE_FLOWS_CARDS_ENDPOINT, {
      params: { templateId, ...filter },
      paramsSerializer: processPerformanceParams,
    });
  },

  getTemplatePrioritizedFlowTable(
    templateId: number,
    filter?: BasePerformanceRequestFilterParams & PaginationParams,
    signal?: AbortSignal,
  ) {
    return axios.get<PrioritizedFlowTableTemplates[]>(TEMPLATE_FLOWS_TABLE_ENDPOINT, {
      params: { templateId, ...filter },
      paramsSerializer: processPerformanceParams,
      signal,
    });
  },
  getTemplateExport(filter: BasePerformanceRequestFilterParams, templateId: number) {
    return axios.get(TEMPLATE_EXPORT_ENDPOINT, {
      params: { ...filter, templateId },
      paramsSerializer: processPerformanceParams,
      responseType: "blob",
    });
  },
  getTemplateOwners(templateId: number, queryParams?: object): Promise<AxiosResponse<GetOwnersResponse>> {
    const query = buildQuery(queryParams);
    return axios.get<GetOwnersResponse>(`/api/templates/${templateId}/owners${query}`);
  },
};
